import { useState } from "react";
import { Download } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import {
  AgtechReleaseReportRequestValidation,
  type AgtechReleasePreset,
  type AgtechReleaseReportRequest,
  useGenerateCfrAgtechReleaseReport,
  useListAgtechReleasePresets,
} from "@/lib/api/cfr";
import { AgtechReleaseRangeBuilder, newAgtechReleaseRange } from "./AgtechReleaseRangeBuilder";

export function CfrAgtechReleaseReportDialog() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [ranges, setRanges] = useState<AgtechReleaseReportRequest["ranges"]>(() => [newAgtechReleaseRange()]);
  const [link, setLink] = useState<string | null>(null);
  const presetsResult = useListAgtechReleasePresets();
  const generateMutation = useGenerateCfrAgtechReleaseReport();

  const presets: AgtechReleasePreset[] = presetsResult.data?.success ? presetsResult.data.data : [];

  const generateReport = async () => {
    const parsed = AgtechReleaseReportRequestValidation.safeParse({ ranges });
    if (!parsed.success) {
      toast({
        title: "Invalid report ranges",
        description: parsed.error.issues[0]?.message ?? "Please correct the selected ranges.",
        variant: "destructive",
      });
      return;
    }

    const result = await generateMutation.mutateAsync(parsed.data);
    if (!result.success) {
      toast({
        title: "Failed to generate report",
        description: result.message || "Please try again.",
        variant: "destructive",
      });
      return;
    }

    setLink(result.data.link);
    toast({ title: "Report generated." });
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open: boolean) => {
        setIsOpen(open);
        if (!open) {
          setLink(null);
          setRanges([newAgtechReleaseRange()]);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">CFR Status Report</Button>
      </DialogTrigger>
      <DialogContent className="flex flex-col lg:max-w-[70vw] max-h-[90vh] p-8">
        <DialogHeader>
          <DialogTitle>CFR Agtech Release Report</DialogTitle>
          <DialogDescription>Select commodities and shipment-date ranges for the report.</DialogDescription>
        </DialogHeader>

        <AgtechReleaseRangeBuilder key={isOpen ? "open" : "closed"} presets={presets} onChange={setRanges} />

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={generateMutation.isLoading}>
            Cancel
          </Button>
          {link ? (
            <Button asChild>
              <a href={link} target="_blank" rel="noreferrer">
                <Download className="h-4 w-4 mr-2" /> Download Report
              </a>
            </Button>
          ) : (
            <Button onClick={generateReport} disabled={generateMutation.isLoading}>
              {generateMutation.isLoading ? "Generating..." : "Generate Report"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
